import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, FileText, RotateCcw, Loader2, Trophy, RefreshCw, PenLine } from "lucide-react";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];
const OPEN_HINTS = ["essay", "paragraph", "response", "multi_part", "multipart", "view"];
const OBJECTIVE = ["short_answer", "numeric", "numerical", "fill_in_blank", "true_false", "multiple_choice", ""];
const problemMode = (p) => {
  if ((p.problem_type || "").toLowerCase().replace(/\s/g, "") === "multiple_choice" || (Array.isArray(p.options) && p.options.length > 0)) return "mc";
  const t = (p.problem_type || "").toLowerCase();
  if (!OBJECTIVE.includes(t) && OPEN_HINTS.some((h) => t.includes(h))) return "open";
  return "text";
};

export function Assignment({ assignment, lastAttempt, preview = false, accent = "#22C55E" }) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const problems = assignment.online_problems || [];
  const gradable = assignment.supports_online && problems.length > 0;

  const submit = async () => {
    if (Object.keys(answers).length < problems.length) {
      toast.error("Please answer every question first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/assignments/${assignment.assignment_id}/submit`, { answers, preview });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setAnswers({}); setResult(null); };

  // Non-interactive assignment — just show the description card
  if (!gradable) {
    return (
      <div className="rounded-2xl bg-muted p-4" data-testid={`assignment-static-${assignment.assignment_id}`}>
        <p className="font-extrabold text-foreground">{assignment.title}</p>
        {assignment.description && <p className="mt-1 text-sm font-semibold text-muted-foreground">{assignment.description}</p>}
        {assignment.total_online_points ? <span className="mt-2 inline-block rounded-full bg-secondary/15 px-3 py-1 text-xs font-extrabold text-secondary">{assignment.total_online_points} pts</span> : null}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-border bg-card p-5 shadow-sm sm:p-6" data-testid={`assignment-card-${assignment.assignment_id}`}>
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-green-100 text-green-600"><FileText className="h-7 w-7" strokeWidth={2.5} /></span>
          <div>
            <p className="font-head text-lg font-black">{assignment.title}</p>
            <p className="text-sm font-bold text-muted-foreground">{problems.length} questions · {assignment.total_online_points || 0} pts · pass 70%</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {lastAttempt && (
            <span className={`rounded-full px-3 py-1.5 text-sm font-extrabold ${lastAttempt.passed ? "bg-green-100 text-green-600" : "bg-muted text-muted-foreground"}`}>
              Last: {lastAttempt.score_pct}% {lastAttempt.passed ? "· Passed" : ""}
            </span>
          )}
          <Button onClick={() => setOpen(true)} className="rounded-full px-7 py-6 text-base font-extrabold text-white tactile" style={{ backgroundColor: accent }} data-testid={`assignment-start-${assignment.assignment_id}`}>
            {lastAttempt ? "Try Again" : "Start Assignment"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-border bg-card p-6 shadow-sm sm:p-8" data-testid={`assignment-open-${assignment.assignment_id}`}>
      <div className="flex items-center justify-between border-b-2 border-border pb-4">
        <h3 className="font-head text-xl font-black">{assignment.title}</h3>
        <button onClick={() => { setOpen(false); reset(); }} className="rounded-full bg-muted px-4 py-1.5 text-sm font-extrabold text-muted-foreground hover:text-primary">Close</button>
      </div>

      {result ? (
        <div className="mt-6" data-testid="assignment-result">
          <div className={`flex items-center gap-4 rounded-3xl border-2 p-6 ${result.passed ? "animate-pop border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}>
            <span className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ${result.passed ? "bg-green-100 text-green-600" : "bg-amber-100 text-secondary"}`}>
              {result.passed ? <Trophy className="h-9 w-9" strokeWidth={2.25} /> : <RefreshCw className="h-8 w-8" strokeWidth={2.5} />}
            </span>
            <div>
              <p className="font-head text-4xl font-black">{result.score_pct}%</p>
              <p className="text-sm font-bold text-muted-foreground">{result.points_earned}/{result.total_points} pts · {result.passed ? "You passed! 🎉" : `Give it another go — you need ${result.passing_score}%`}</p>
              {result.preview && <p className="mt-1 text-xs font-bold text-muted-foreground" data-testid="assignment-preview-note">Preview mode — this score was not saved.</p>}
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {result.results.map((r, i) => {
              const neutral = r.is_correct === null;
              return (
                <div key={i} className={`rounded-2xl border-2 p-4 ${neutral ? "border-sky-200 bg-sky-50" : r.is_correct ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                  <div className="flex items-start gap-3">
                    {neutral ? <PenLine className="mt-0.5 h-6 w-6 shrink-0 text-sky-600" strokeWidth={2.5} /> : r.is_correct ? <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" strokeWidth={2.5} /> : <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-500" strokeWidth={2.5} />}
                    <div className="min-w-0">
                      <p className="font-extrabold">{r.question}</p>
                      <p className="mt-1 text-sm font-bold text-muted-foreground">Your answer: <span className={neutral ? "text-sky-700" : r.is_correct ? "text-green-600" : "text-red-500"}>{String(r.your_answer ?? "—")}</span></p>
                      {neutral ? (
                        <p className="mt-1 text-sm font-bold text-muted-foreground">Sample answer: <span className="text-sky-700">{r.correct_answer}</span> <span className="ml-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-extrabold text-sky-700">Self-check</span></p>
                      ) : !r.is_correct ? (
                        <p className="text-sm font-bold text-muted-foreground">Correct: <span className="text-green-600">{r.correct_answer}</span></p>
                      ) : null}
                      {r.explanation && <p className="mt-2 text-sm font-semibold text-foreground/80">{r.explanation}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={reset} className="mt-6 rounded-full px-7 py-6 text-base font-extrabold text-white tactile" style={{ backgroundColor: accent }} data-testid="assignment-retake"><RotateCcw className="mr-2 h-5 w-5" strokeWidth={2.5} /> Try Again</Button>
        </div>
      ) : (
        <div className="mt-6 space-y-7">
          {assignment.description && <p className="rounded-2xl bg-muted p-4 text-sm font-semibold text-muted-foreground">{assignment.description}</p>}
          {problems.map((p, i) => {
            const mode = problemMode(p);
            return (
              <div key={p.problem_id || i} data-testid={`assignment-q-${i}`}>
                <p className="text-lg font-extrabold"><span className="mr-2 text-primary">{i + 1}.</span>{p.question_text}{p.points ? <span className="ml-2 text-sm font-bold text-muted-foreground">({p.points} pts)</span> : null}</p>
                {p.hint && <p className="mt-1 text-sm font-semibold italic text-muted-foreground">Hint: {p.hint}</p>}
                {mode === "mc" ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {(p.options || []).map((opt, oi) => {
                      const selected = answers[i] === opt;
                      return (
                        <button
                          key={oi}
                          onClick={() => setAnswers({ ...answers, [i]: opt })}
                          data-testid={`assignment-opt-${i}-${oi}`}
                          className={`flex min-h-[64px] w-full items-center gap-3 rounded-2xl border-2 p-4 text-left text-base font-extrabold transition-all duration-150 hover:-translate-y-0.5 ${selected ? "border-primary bg-primary/10 text-foreground shadow-sm" : "border-border bg-card hover:border-primary/50"}`}
                        >
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-black ${selected ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{LETTERS[oi] || oi + 1}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : mode === "open" ? (
                  <textarea
                    value={answers[i] || ""}
                    onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                    placeholder="Write your answer — this one is self-checked against a sample answer"
                    rows={4}
                    data-testid={`assignment-textarea-${i}`}
                    className="mt-3 w-full rounded-2xl border-2 border-border bg-background p-4 text-base font-semibold outline-none focus:border-primary"
                  />
                ) : (
                  <Input
                    value={answers[i] || ""}
                    onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })}
                    placeholder="Type your answer"
                    data-testid={`assignment-input-${i}`}
                    className="mt-3 rounded-2xl border-2 border-border bg-background py-6 text-base font-semibold"
                  />
                )}
              </div>
            );
          })}
          <Button onClick={submit} disabled={busy} className="w-full rounded-full py-7 text-lg font-extrabold text-white tactile" style={{ backgroundColor: accent }} data-testid={`assignment-submit-${assignment.assignment_id}`}>
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : "Check My Answers!"}
          </Button>
        </div>
      )}
    </div>
  );
}
